import React, { useEffect, useState, useCallback } from 'react';
import { Row, Col, Card, Table, Spinner, Alert, Form, InputGroup, Pagination } from 'react-bootstrap';
import apiService from '../services/api';

const EnterpriseLoginAudit = () => {
  const [successfulLogins, setSuccessfulLogins] = useState([]);
  const [unsuccessfulLogins, setUnsuccessfulLogins] = useState([]);
  const [loadingSuccess, setLoadingSuccess] = useState(true);
  const [loadingUnsuccess, setLoadingUnsuccess] = useState(true);
  const [errorSuccess, setErrorSuccess] = useState(null);
  const [errorUnsuccess, setErrorUnsuccess] = useState(null);

  // Search terms
  const [successSearchTerm, setSuccessSearchTerm] = useState('');
  const [unsuccessSearchTerm, setUnsuccessSearchTerm] = useState('');

  // Sorting state for successful logins
  const [successSort, setSuccessSort] = useState({ column: null, direction: 'asc' });
  // Sorting state for unsuccessful logins
  const [unsuccessSort, setUnsuccessSort] = useState({ column: null, direction: 'asc' });

  // Pagination state for successful logins
  const [successPage, setSuccessPage] = useState(1);
  const [successLimit, setSuccessLimit] = useState(50);
  const [successPagination, setSuccessPagination] = useState({ total_records: 0, page: 1, limit: 50, total_pages: 1 });

  // Pagination state for unsuccessful logins
  const [unsuccessPage, setUnsuccessPage] = useState(1);
  const [unsuccessLimit, setUnsuccessLimit] = useState(50);
  const [unsuccessPagination, setUnsuccessPagination] = useState({ total_records: 0, page: 1, limit: 50, total_pages: 1 });

  const fetchSuccessfulLogins = useCallback((page, limit, search) => {
    setLoadingSuccess(true);
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });
    apiService.get(`/enterprise_login_clearance?${queryParams}`)
      .then((response) => {
        setSuccessfulLogins(response.data || response);
        if (response.pagination) {
          setSuccessPagination(response.pagination);
        }
        setLoadingSuccess(false);
      })
      .catch((err) => {
        setErrorSuccess(err.message || 'Failed to fetch successful logins');
        setLoadingSuccess(false);
      });
  }, []);

  const fetchUnsuccessfulLogins = useCallback((page, limit, search) => {
    setLoadingUnsuccess(true);
    const queryParams = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
      ...(search && { search })
    });
    apiService.get(`/enterprise_login_attempt/unsuccessful?${queryParams}`)
      .then((response) => {
        setUnsuccessfulLogins(response.data || response);
        if (response.pagination) {
          setUnsuccessPagination(response.pagination);
        }
        setLoadingUnsuccess(false);
      })
      .catch((err) => {
        setErrorUnsuccess(err.message || 'Failed to fetch unsuccessful logins');
        setLoadingUnsuccess(false);
      });
  }, []);

  useEffect(() => {
    fetchSuccessfulLogins(successPage, successLimit, successSearchTerm);
  }, [successPage, successLimit, successSearchTerm, fetchSuccessfulLogins]);

  useEffect(() => {
    fetchUnsuccessfulLogins(unsuccessPage, unsuccessLimit, unsuccessSearchTerm);
  }, [unsuccessPage, unsuccessLimit, unsuccessSearchTerm, fetchUnsuccessfulLogins]);


  let filteredSuccessfulLogins = [...successfulLogins];

  // Sort successful logins
  if (successSort.column) {
    filteredSuccessfulLogins = [...filteredSuccessfulLogins].sort((a, b) => {
      let aVal = a[successSort.column];
      let bVal = b[successSort.column];
      // Handle null/undefined
      if (aVal === undefined || aVal === null) aVal = '';
      if (bVal === undefined || bVal === null) bVal = '';
      // For string comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return successSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return successSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  let filteredUnsuccessfulLogins = [...unsuccessfulLogins];

  // Sort unsuccessful logins
  if (unsuccessSort.column) {
    filteredUnsuccessfulLogins = [...filteredUnsuccessfulLogins].sort((a, b) => {
      let aVal = a[unsuccessSort.column];
      let bVal = b[unsuccessSort.column];
      if (aVal === undefined || aVal === null) aVal = '';
      if (bVal === undefined || bVal === null) bVal = '';
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        aVal = aVal.toLowerCase();
        bVal = bVal.toLowerCase();
      }
      if (aVal < bVal) return unsuccessSort.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return unsuccessSort.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Table header click handlers
  const handleSuccessSort = (column) => {
    setSuccessSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleUnsuccessSort = (column) => {
    setUnsuccessSort(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleSuccessPageChange = (page) => {
    setSuccessPage(page);
  };

  const handleUnsuccessPageChange = (page) => {
    setUnsuccessPage(page);
  };

  const handleSuccessLimitChange = (e) => {
    setSuccessLimit(parseInt(e.target.value));
    setSuccessPage(1);
  };

  const handleUnsuccessLimitChange = (e) => {
    setUnsuccessLimit(parseInt(e.target.value));
    setUnsuccessPage(1);
  };

  const renderPagination = (pagination, currentPage, onPageChange) => {
    if (pagination.total_pages <= 1) return null;
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(pagination.total_pages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return (
      <Pagination className="mb-0">
        <Pagination.First disabled={currentPage === 1} onClick={() => onPageChange(1)} />
        <Pagination.Prev disabled={currentPage === 1} onClick={() => onPageChange(currentPage - 1)} />
        {start > 1 && <Pagination.Ellipsis disabled />}
        {pages.map(p => (
          <Pagination.Item key={p} active={p === currentPage} onClick={() => onPageChange(p)}>
            {p}
          </Pagination.Item>
        ))}
        {end < pagination.total_pages && <Pagination.Ellipsis disabled />}
        <Pagination.Next disabled={currentPage === pagination.total_pages} onClick={() => onPageChange(currentPage + 1)} />
        <Pagination.Last disabled={currentPage === pagination.total_pages} onClick={() => onPageChange(pagination.total_pages)} />
      </Pagination>
    );
  };

  return (
    <div>
      <Row>
        <Col>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2>
              <i className="bi bi-file-text me-2"></i>
              Enterprise Audit Logs
            </h2>
          </div>
        </Col>
      </Row>

      {/* Successful Logins Section */}
      <Row className="mb-5">
        <Col>
          <Card>
            <Card.Header className="bg-success text-white d-flex justify-content-between align-items-center">
              <span>Successful Enterprise Logins</span>
              <div className="d-flex align-items-center">
                <Form.Label className="text-white mb-0 me-2" style={{ fontSize: '0.85rem' }}>Per page:</Form.Label>
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={successLimit}
                  onChange={handleSuccessLimitChange}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body>
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search successful logins..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setSuccessPage(1);
                      setSuccessSearchTerm(e.target.value);
                    }
                  }}
                  onBlur={(e) => {
                    setSuccessPage(1);
                    setSuccessSearchTerm(e.target.value);
                  }}
                />
              </InputGroup>
              {loadingSuccess ? (
                <div className="text-center py-4"><Spinner animation="border" /> Loading...</div>
              ) : errorSuccess ? (
                <Alert variant="danger">{errorSuccess}</Alert>
              ) : (
                <div style={{ maxHeight: '33vh', overflowY: 'auto' }}>
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('login_attempt_id')}>
                          Login Attempt ID {successSort.column === 'login_attempt_id' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('user_full_name')}>
                          Name {successSort.column === 'user_full_name' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('email')}>
                          Email {successSort.column === 'email' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('is_person')}>
                          Is Person {successSort.column === 'is_person' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('is_active')}>
                          Is Active {successSort.column === 'is_active' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('is_locked')}>
                          Is Locked {successSort.column === 'is_locked' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('identifier_attempted')}>
                          Identifier Attempted {successSort.column === 'identifier_attempted' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('ip_address')}>
                          IP Address {successSort.column === 'ip_address' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('attempt_timestamp')}>
                          Timestamp {successSort.column === 'attempt_timestamp' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('session_duration_minutes')}>
                          Session Duration (min) {successSort.column === 'session_duration_minutes' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('session_active')}>
                          Session Active {successSort.column === 'session_active' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('app_version')}>
                          App Version {successSort.column === 'app_version' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleSuccessSort('build_number')}>
                          Build Number {successSort.column === 'build_number' ? (successSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(filteredSuccessfulLogins) && filteredSuccessfulLogins.length > 0 ? (
                        filteredSuccessfulLogins.map((row) => (
                          <tr key={row.login_attempt_id}>
                            <td>{row.login_attempt_id}</td>
                            <td>{row.user_full_name}</td>
                            <td>{row.email}</td>
                            <td>{row.is_person ? 'Yes' : 'No'}</td>
                            <td>{row.is_active ? 'Yes' : 'No'}</td>
                            <td>{row.is_locked ? 'Yes' : 'No'}</td>
                            <td>{row.identifier_attempted}</td>
                            <td>{row.ip_address}</td>
                            <td>{row.attempt_timestamp ? apiService.formatDateTime(row.attempt_timestamp) : ''}</td>
                            <td>{row.session_duration_minutes}</td>
                            <td>{row.session_active ? 'Active' : 'Inactive'}</td>
                            <td>{row.app_version}</td>
                            <td>{row.build_number}</td>
                          </tr>
                        ))
                      ) : (
                        <tr><td colSpan={13} className="text-center">
                          {successSearchTerm ? 'No matching successful logins found.' : 'No successful logins found.'}
                        </td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
              {successPagination.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Showing {((successPagination.page - 1) * successPagination.limit) + 1} to {Math.min(successPagination.page * successPagination.limit, successPagination.total_records)} of {successPagination.total_records} entries
                  </div>
                  {renderPagination(successPagination, successPage, handleSuccessPageChange)}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Unsuccessful Logins Section */}
      <Row>
        <Col>
          <Card>
            <Card.Header className="bg-danger text-white d-flex justify-content-between align-items-center">
              <span>Unsuccessful Enterprise Login Attempts</span>
              <div className="d-flex align-items-center">
                <Form.Label className="text-white mb-0 me-2" style={{ fontSize: '0.85rem' }}>Per page:</Form.Label>
                <Form.Select
                  size="sm"
                  style={{ width: 'auto' }}
                  value={unsuccessLimit}
                  onChange={handleUnsuccessLimitChange}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body>
              <InputGroup className="mb-3">
                <InputGroup.Text>
                  <i className="bi bi-search"></i>
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="Search unsuccessful logins..."
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      setUnsuccessPage(1);
                      setUnsuccessSearchTerm(e.target.value);
                    }
                  }}
                  onBlur={(e) => {
                    setUnsuccessPage(1);
                    setUnsuccessSearchTerm(e.target.value);
                  }}
                />
              </InputGroup>
              {loadingUnsuccess ? (
                <div className="text-center py-4"><Spinner animation="border" /> Loading...</div>
              ) : errorUnsuccess ? (
                <Alert variant="danger">{errorUnsuccess}</Alert>
              ) : (
                <div style={{ maxHeight: '33vh', overflowY: 'auto' }}>
                  <Table striped bordered hover responsive size="sm">
                    <thead>
                      <tr>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('login_attempt_id')}>
                          Login Attempt ID {unsuccessSort.column === 'login_attempt_id' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('identifier_attempted')}>
                          Identifier Attempted {unsuccessSort.column === 'identifier_attempted' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('ip_address')}>
                          IP Address {unsuccessSort.column === 'ip_address' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('attempt_timestamp')}>
                          Timestamp {unsuccessSort.column === 'attempt_timestamp' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('attempt_result')}>
                          Result {unsuccessSort.column === 'attempt_result' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('failure_reason')}>
                          Failure Reason {unsuccessSort.column === 'failure_reason' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('user_full_name')}>
                          Matched Name {unsuccessSort.column === 'user_full_name' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('email')}>
                          Matched Email {unsuccessSort.column === 'email' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('is_person')}>
                          Is Person {unsuccessSort.column === 'is_person' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('is_active')}>
                          Is Active {unsuccessSort.column === 'is_active' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('is_locked')}>
                          Is Locked {unsuccessSort.column === 'is_locked' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                        <th style={{ cursor: 'pointer' }} onClick={() => handleUnsuccessSort('password_change_required')}>
                          Password Change Required {unsuccessSort.column === 'password_change_required' ? (unsuccessSort.direction === 'asc' ? '▲' : '▼') : ''}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {Array.isArray(filteredUnsuccessfulLogins) && filteredUnsuccessfulLogins.length > 0 ? (
                        filteredUnsuccessfulLogins.map((row) => (
                          <tr key={row.login_attempt_id}>
                            <td>{row.login_attempt_id}</td>
                            <td>{row.identifier_attempted}</td>
                            <td>{row.ip_address}</td>
                            <td>{row.attempt_timestamp ? apiService.formatDateTime(row.attempt_timestamp) : ''}</td>
                            <td>{row.attempt_result}</td>
                            <td>{row.failure_reason}</td>
                            <td>{row.user_full_name}</td>
                            <td>{row.email}</td>
                            <td>
                              {row.is_person === null ? '' : row.is_person ? 'Yes' : 'No'}
                            </td>
                            <td>{row.is_active === null ? '' : row.is_active ? 'Yes' : 'No'}</td>
                            <td>{row.is_locked === null ? '' : row.is_locked ? 'Yes' : 'No'}</td>
                            <td>{row.password_change_required === null ? '' : row.password_change_required ? 'Yes' : 'No'}</td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={11} className="text-center">
                            {unsuccessSearchTerm ? 'No matching unsuccessful logins found.' : 'No unsuccessful logins found.'}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              )}
              {unsuccessPagination.total_pages > 1 && (
                <div className="d-flex justify-content-between align-items-center mt-3">
                  <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                    Showing {((unsuccessPagination.page - 1) * unsuccessPagination.limit) + 1} to {Math.min(unsuccessPagination.page * unsuccessPagination.limit, unsuccessPagination.total_records)} of {unsuccessPagination.total_records} entries
                  </div>
                  {renderPagination(unsuccessPagination, unsuccessPage, handleUnsuccessPageChange)}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default EnterpriseLoginAudit;
